import { features, useSelector, useThunkDispatch } from '@tunasong/redux'
import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { Button } from '../primitives/button.js'
import { Snackbar } from '../primitives/snackbar.js'

export interface ActionToast {
  message: string
  actionLabel?: string
  onAction?: () => void
  /** ms; 6000 is the Undo window (request-access spec §8.4) */
  duration?: number
}

interface OpenToast {
  toast: ActionToast
  /** The global alert that was showing when this toast opened; only a different one closes it */
  alertAtOpen: unknown
}

/**
 * A snackbar whose action runs a callback, for Undo and Retry. The global NotificationAlert's actions can only
 * navigate, and a callback must not be stored in redux, so this renders locally.
 *
 * One toast at a time, newest wins (request-access spec §8.9 #5): opening dismisses the global alert, and a new
 * global alert closes this toast.
 */
export const useActionToast = () => {
  const dispatch = useThunkDispatch()
  const alert = useSelector(state => state.notifications.alert)
  const [current, setCurrent] = useState<OpenToast | null>(null)

  const close = () => setCurrent(null)

  const showToast = (toast: ActionToast) => {
    setCurrent({ toast, alertAtOpen: alert })
    dispatch(features.notifications.actions.setAlert(null))
  }

  const supersededByAlert = Boolean(current && alert && alert !== current.alertAtOpen)
  useEffect(() => {
    if (supersededByAlert) {
      setCurrent(null)
    }
  }, [supersededByAlert])

  const handleAction = () => {
    const action = current?.toast.onAction
    close()
    action?.()
  }

  const toast: ReactNode =
    current && !supersededByAlert ? (
      <Snackbar
        open
        onClose={close}
        autoHideDuration={current.toast.duration ?? 6000}
        message={current.toast.message}
        action={
          current.toast.actionLabel ? (
            <Button size="small" onClick={handleAction}>
              {current.toast.actionLabel}
            </Button>
          ) : null
        }
      />
    ) : null

  return { showToast, toast }
}
